import React, { useCallback, useState } from 'react';
import { Paper, makeStyles, Button } from '@material-ui/core';
import clsx from 'clsx';
import { useModePanelStyle, BaseInfoCompProps, InfoCompPanelHeader, useModeFormStyle, FontIcon, BaseEntityField, ModeInfoCompTextInputField,
    usePropValue, inputStateSetterHelper, BaseInfoCompActionsSet, BaseInfoCompSaveAction, ModeFormInputSection, EntityFieldsCollection,
    ModeInfoCompAutoCompleteInputField, ModeInfoCompCheckBoxInputField, ModeInfoCompSelectInputField } from '@moderepo/mode-app-components';
import { MetricMetadata, HeatMapDashboardWidgetInputs, WidgetDataViewLayout, TSDBDashboardWidgetDataSourceInputs } from '../../containers';
import { TSDBDashboardWidgetDataSourceInputsComp } from './TSDBDashboardWidgetDataSourceInputsComp';
import { BaseDashboardWidgetSettingsCompFields, DataDashboardWidgetSettingsCompFields, TSDBDashboardWidgetSettingsFields,
    TSDBDashboardWidgetSettingsCompTexts } from './dashboardCompUtils';



const useStyle = makeStyles(() => {
    return {
        root: {
        },
    };
}, { name: 'HeatMapDashboardWidgetSettingsComp', index: 1 });



/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface HeapMapDashboardWidgetActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate?: BaseInfoCompSaveAction<HeatMapDashboardWidgetInputs> | undefined
}



/**
 * This is the set of metrics metadata we need for the Data source settings
 */
const editableDataSourceMetadata: readonly (keyof MetricMetadata)[] = [
    'displayName', 'unit',
    'lowValueCriticalThreshold', 'lowValueWarningThreshold',
    'highValueWarningThreshold', 'highValueCriticalThreshold',
    'conversionFormula',
];



// Extends the fields defined in BaseDashboardWidget , DataDashboardWidget, TSDBDashboardWidget and add additional fields
interface WidgetSettingsFields
    extends EntityFieldsCollection, BaseDashboardWidgetSettingsCompFields, DataDashboardWidgetSettingsCompFields, TSDBDashboardWidgetSettingsFields {

    readonly view: {
        readonly viewLayout: BaseEntityField<WidgetDataViewLayout>;
        readonly showLegends: BaseEntityField<boolean>;
    }
}


export interface HeatMapDashboardWidgetSettingsCompProps extends BaseInfoCompProps {
    readonly fields: WidgetSettingsFields;
    readonly texts: TSDBDashboardWidgetSettingsCompTexts;
    readonly actions?: HeapMapDashboardWidgetActionsSet | undefined;
}



/**
 * This is a DUMB Dashboard Widget component which is used for displaying the SETTINGS for the HeapMapDashboardWidget.
 * This component does not have any logic. All it does is displays data based on the props provided by the container. All actions done in
 * this component will be dispatched back to the container's handler.
 */
export const HeatMapDashboardWidgetSettingsComp: React.FC<HeatMapDashboardWidgetSettingsCompProps> = (
    props: HeatMapDashboardWidgetSettingsCompProps,
) => {

    const [widgetTitleInput, setWidgetTitleInput] = usePropValue<string | undefined>(props.fields.title.value);
    const [widgetIconInput, setWidgetIconInput] = usePropValue<string | undefined>(props.fields.icon.value);
    const [reloadDataFreqInput, setReloadDataFreqInput] = usePropValue<number | undefined>(props.fields.reloadDataFreq.value);
    const [viewLayoutInput, setViewLayoutInput] = usePropValue<WidgetDataViewLayout | undefined>(props.fields.view.viewLayout.value);
    const [showLegendsInput, setShowLegendsInput] = usePropValue<boolean | undefined>(props.fields.view.showLegends.value);

    // The cached data source input values
    const [dataSourceInputs, setDataSourceInputs] = useState<TSDBDashboardWidgetDataSourceInputs>({});

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {

        inputStateSetterHelper(inputName, value, {
            title         : setWidgetTitleInput,
            icon          : setWidgetIconInput,
            reloadDataFreq: setReloadDataFreqInput,
            viewLayout    : setViewLayoutInput,
            showLegends   : setShowLegendsInput,
        });

    }, [setWidgetTitleInput, setWidgetIconInput, setReloadDataFreqInput, setViewLayoutInput, setShowLegendsInput]);



    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {

        setWidgetTitleInput(props.fields.title.value);
        setWidgetIconInput(props.fields.icon.value);
        setReloadDataFreqInput(props.fields.reloadDataFreq.value);
        setViewLayoutInput(props.fields.view.viewLayout.value);
        setShowLegendsInput(props.fields.view.showLegends.value);

    }, [setWidgetTitleInput, props.fields.title.value, props.fields.icon.value, props.fields.reloadDataFreq.value, props.fields.view.viewLayout.value,
        props.fields.view.showLegends.value, setWidgetIconInput, setReloadDataFreqInput, setViewLayoutInput, setShowLegendsInput]);



    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {

            props.actions.saveUpdate.onClick({
                title         : widgetTitleInput?.trim(),
                icon          : widgetIconInput?.trim(),
                reloadDataFreq: reloadDataFreqInput,
                viewLayout    : viewLayoutInput,
                showLegends   : showLegendsInput,
                ...dataSourceInputs,
            });
        }

        return false;
    }, [props.actions, widgetTitleInput, widgetIconInput, reloadDataFreqInput, viewLayoutInput, showLegendsInput, dataSourceInputs]);



    /**
     * Handler when the entire data source input changed. We will cache the inputs value and use it for when the form submit.
     */
    const onDataSourceInputsUpdated = useCallback((inputs: TSDBDashboardWidgetDataSourceInputs) => {
        setDataSourceInputs(inputs);
    }, []);



    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, classes.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <ModeInfoCompTextInputField
                        className={formClasses.formInputField}
                        inputType="text"
                        compProps={props}
                        inputProps={props.fields.title}
                        fieldName="title"
                        inputValue={widgetTitleInput}
                        onInputChange={onInputChange}
                    />
                    <ModeInfoCompAutoCompleteInputField
                        className={formClasses.formInputField}
                        fieldName="icon"
                        compProps={props}
                        inputProps={props.fields.icon}
                        inputValue={widgetIconInput}
                        onInputChange={onInputChange}
                    />
                    <ModeInfoCompSelectInputField
                        className={formClasses.formInputField}
                        compProps={props}
                        inputProps={props.fields.reloadDataFreq}
                        fieldName="reloadDataFreq"
                        inputValue={reloadDataFreqInput}
                        onInputChange={onInputChange}
                    />
                    <ModeFormInputSection
                        collapsible
                        title={props.texts.dataSourceSettings}
                        icon="fas fa-database"
                    >
                        <TSDBDashboardWidgetDataSourceInputsComp
                            isEditing={props.isEditing}
                            fields={props.fields.dataSource}
                            texts={props.texts.metricsMetadataLabels}
                            editableMetadata={editableDataSourceMetadata}
                            onFieldClicked={props.onFieldClicked}   // onFieldClicked, propagate it to the container
                            onFormInputChange={props.onFormInputChange}
                            onDataSourceInputsUpdated={onDataSourceInputsUpdated}
                        />
                    </ModeFormInputSection>

                    <ModeFormInputSection
                        collapsible
                        title={props.texts.viewSettings}
                        icon="visibility"
                    >
                        <>
                            <div className={clsx(formClasses.formInputGroup, 'xx-small')}>
                                <ModeInfoCompSelectInputField
                                    className={formClasses.formInputField}
                                    compProps={props}
                                    inputProps={props.fields.view.viewLayout}
                                    fieldName="viewLayout"
                                    inputValue={viewLayoutInput}
                                    onInputChange={onInputChange}
                                />
                                <ModeInfoCompCheckBoxInputField
                                    compProps={props}
                                    inputProps={props.fields.view.showLegends}
                                    fieldName="showLegends"
                                    className={formClasses.formInputField}
                                    inputValue={showLegendsInput}
                                    onInputChange={onInputChange}
                                />
                            </div>
                        </>
                    </ModeFormInputSection>

                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} /> : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} /> : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>
        </Paper>
    );

};
